<template>
  <nuttrema-layout-three-vertical class="subscription-east-summary" :class="[ theme ? `theme-${theme}` : '' ]">

    <div slot="middle" class="subscription-east-summary__content">

      <div class="subscription-east-summary__title">
        {{ $t('label.selected_plan_and_features') }}
      </div>

      <div class="subscription-east-summary__item">
        <div class="item__label">
          <div class="item__title">{{ $t('label.plan') }}</div>
          <div class="item__value">
            <a @click="show.planDescription = !show.planDescription">
              <i class="far fa-angle-up" v-if="show.planDescription"></i>
              <i class="far fa-angle-down" v-else></i>
            </a>
            <strong>{{ _get(summary, 'group.name') | slice(24) }} <template v-if="_get(summary, 'group.name') !== _get(summary, 'name')">({{ _get(summary, 'name') }})</template></strong>
          </div>
        </div>
        <div class="item__description" v-if="show.planDescription">
          <p>{{ $t('label.included_features') }}</p>
          <ul>
            <li v-for="code in _get(summary, 'features', [])">
              <strong>{{ _get(code, 'feature.name') }}</strong> - <span v-for="(featureOption, index) in _get(code, 'values', [])"><template v-if="index !== 0">, </template>{{ featureOption.description }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div v-if="preconditions" class="subscription-east-summary__item">
        <div class="item__label">
          <div class="item__title">{{ $t('label.subscription_start') }}</div>
          <div class="item__value">
            {{ _get(duration, 'start_on') | moment('ll') }}
          </div>
        </div>
      </div>

      <div class="subscription-east-summary__item">
        <div class="item__label">
          <div class="item__title">{{ $t('label.subscription_end') }}</div>
          <div class="item__value">
            {{ _get(duration, 'end_on') | moment('ll') }}
          </div>
        </div>
      </div>

      <div class="subscription-east-summary__item">
        <div class="item__label">
            <div class="item__title">{{ $t('label.price') }}</div>
            <div class="item__value">
              {{ _get(invoiceItemAmounts, 'price.preTaxAmount', 0) | number-format(2, false, true) }} {{ _get(priceOption, 'Currency.symbol') }}
            </div>
        </div>
      </div>

      <div class="subscription-east-summary__item">
        <div class="item__label">
            <div class="item__title">{{ $t('label.extra_options') }}</div>
            <div class="item__value">
              {{ _get(invoiceItemAmounts, 'extraOptions.preTaxAmount', 0) | number-format(2, false, true) }} {{ _get(priceOption, 'Currency.symbol') }}
            </div>
        </div>
      </div>

      <div class="subscription-east-summary__item is-sub-total">
        <div class="item__label">
            <div class="item__title">{{ $t('label.sub_total') }}</div>
            <div class="item__value">
              {{ _get(invoiceItemAmounts, 'subTotal', 0) | number-format(2, false, true) }} {{ _get(priceOption, 'Currency.symbol') }}
            </div>
        </div>
      </div>

      <div class="subscription-east-summary__item">
        <div class="item__label">
            <div class="item__title">
              <el-tooltip class="item" effect="dark" :content="$t('label.vat_help')" placement="top">
                <span>{{ $t('label.vat') }}
                <template v-if="taxRate"> ({{ taxRate }}%)*</template>
                </span>
              </el-tooltip>
            </div>
            <div class="item__value">
              {{ _get(invoiceItemAmounts, 'vat', 0) | number-format(2, false, true) }} {{ _get(priceOption, 'Currency.symbol') }}
            </div>
        </div>
      </div>

      <div class="subscription-east-summary__item is-total">
        <div class="item__label">
            <div class="item__title">{{ $t('label.total') }}</div>
            <div class="item__value">
              {{ _get(invoiceItemAmounts, 'total', 0) | number-format(2, false, true) }} {{ _get(priceOption, 'Currency.symbol') }}
            </div>
        </div>
      </div>

      <div v-if="!bottomSummary" class="subscription-east-summary__item">
        <div class="item__actions">
          <el-button
             type="nuttrema-orange"
             class="w-100 is-icon-ml-050"
            :loading="processing"
            @click="nextStep">
            {{ $t('label.proceed_to_next_step') }}<i class="far fa-chevron-right"></i>
          </el-button>
          <el-button
             size="small"
             type="text"
             class="w-100 is-icon-mr-050"
            @click="previousStep">
            <i class="far fa-chevron-left"></i>{{ $t('label.back_to_previous_step') }}
          </el-button>
        </div>
      </div>
    </div>

    <div slot="bottom" class="subscription-east-summary__footer">
      <slot name="summary-footer"></slot>
      <template v-if="bottomSummary">
        <el-button
           type="nuttrema-orange"
           class="w-100 is-icon-ml-050"
          :loading="processing"
          @click="nextStep">
          {{ $t('label.proceed_to_next_step') }} <i class="far fa-chevron-right"></i>
        </el-button>
        <el-button
           size="small"
           type="text"
           class="w-100"
          @click="cancel">
          {{ $t('label.cancel_purchase') }}
        </el-button>
      </template>
    </div>

  </nuttrema-layout-three-vertical>
</template>

<script>
import moment from 'moment';
import { get, find, mapValues, each, first } from 'lodash';

import SubscriptionEastSummary from './abstract-subscription-east-summary';

export default {
  name: 'cmpt-subscription-east-summary-select-plan',
  mixins: [SubscriptionEastSummary],
  props: {
    bottomSummary: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  methods: {
    checkPaymenPlanAndCurrency(item) {
      const paymentCheck = get(item, 'PaymentPlan.id') === this.paymentPlanID;
      const currencyCheck = get(item, 'Currency.id') === this.currencyID;
      return paymentCheck && currencyCheck;
    },
    calculateExtraFeaturesPrice() {
      const features = get(this.summary, 'features', []);

      const prices = mapValues(features, (feature) => {
        const priceOption = find(feature.values, (featureItem) => {
          const priceOptions = get(featureItem, 'PriceOptions', []);
          return find(priceOptions, item => this.checkPaymenPlanAndCurrency(item));
        });

        return first(get(priceOption, 'PriceOptions'));
      });

      let sum = 0;

      each(prices, (item) => {
        const price = get(item, 'price', 0);
        sum += Number(price);
      });

      return this.calculateInvoiceItemAmounts(sum, this.taxRate);
    },
  },
  computed: {
    priceOption() {
      const priceOptions = get(this.summary, 'group.PriceOptions') || get(this.summary, 'PriceOptions', []);
      return find(priceOptions, item => this.checkPaymenPlanAndCurrency(item));
    },
    duration() {
      const startOn = get(this.preconditions, 'start_on') || new Date();
      const duration = get(this.priceOption, 'PaymentPlan.months', 0);
      const endOn = moment(startOn).add(duration, 'months');
      return {
        start_on: moment(startOn).format('YYYY-MM-DD'),
        end_on: moment(endOn).format('YYYY-MM-DD'),
      };
    },
  },
};
</script>




// WEBPACK FOOTER //
// src/components/ui/subscription/cmpt-subscription-east-summary-select-plan.vue